import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { I18nService, SupportedLocale } from '../../core/i18n/i18n.service';
import { AuthService } from '../../core/auth/auth.service';
import { UserIdleLockService } from '../../core/auth/user-idle-lock.service';
import { UserExperiencePreferences, UserSelfServiceApi } from '../../core/user/user-self-service.api';
import { PORTAL_SHELL_CONFIG, PortalNavGroup, PortalNavItem, PortalShellConfig } from '../../core/shell/portal-shell.models';
import { ShellSessionService } from '../../core/shell/shell-session.service';
import { DeskPlusAccent, DeskPlusTheme, UserPreferencesService } from '../../core/preferences/user-preferences.service';
import { ShellIconComponent } from '../../shared/shell-icon/shell-icon.component';

interface PortalDestination {
  portalCode?: string;
  routePath?: string;
  displayName?: string;
  tenantId?: string | null;
  isPreferredPortal?: boolean;
}

interface UserNotification {
  id: string; moduleCode: string; eventType: string; entityType: string; entityId?: string | null;
  title: string; description: string; severity: 'INFO' | 'WARNING' | 'CRITICAL';
  routePath: string; occurredAt: string; readAt?: string | null;
}

@Component({
  selector: 'app-application-shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ShellIconComponent],
  templateUrl: './application-shell.component.html',
  styleUrl: './application-shell.component.scss'
})
export class ApplicationShellComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly userSelfService = inject(UserSelfServiceApi);
  private readonly idleLock = inject(UserIdleLockService);

  readonly session = inject(ShellSessionService);
  readonly i18n = inject(I18nService);
  readonly preferences = inject(UserPreferencesService);

  readonly mobileSidebarOpen = signal(false);
  readonly sidebarCollapsed = signal(localStorage.getItem('deskplus.sidebar.collapsed') === '1');
  readonly userMenuOpen = signal(false);
  readonly portalMenuOpen = signal(false);
  readonly preferencesOpen = signal(false);
  readonly languageMenuOpen = signal(false);
  readonly notificationMenuOpen = signal(false);
  readonly searchOpen = signal(false);
  readonly searchQuery = signal('');
  readonly collapsedGroups = signal<Record<number, boolean>>({});
  readonly currentUrl = signal(this.router.url);
  readonly fullscreen = signal(!!document.fullscreenElement);
  readonly profilePhotoUrl = signal<string | null>(null);
  readonly authorizedDestinations = signal<PortalDestination[]>([]);
  readonly unreadNotifications = signal(0);
  readonly notificationAlerts = signal<UserNotification[]>([]);
  readonly notificationsLoading = signal(false);
  readonly effectivePermissions = signal<Set<string>>(new Set());
  readonly effectiveRoles = signal<Set<string>>(new Set());
  readonly experiencePreferences = signal<UserExperiencePreferences | null>(null);
  readonly screenLockMinutes = signal(15);
  readonly lockingSession = signal(false);

  private readonly experienceUpdateHandler = (event: Event) => {
    const update =
      event as CustomEvent<UserExperiencePreferences>;

    if (update.detail) {
      this.applyExperiencePreferences(update.detail);
    }
  };

  readonly portal = computed<PortalShellConfig>(() => {
    const url = this.currentUrl();
    if (url.startsWith('/app/niotech')) return PORTAL_SHELL_CONFIG.PLATFORM_ADMIN;
    if (url.startsWith('/app/technician')) return PORTAL_SHELL_CONFIG.TECHNICIAN_PORTAL;
    if (url.startsWith('/app/services')) return PORTAL_SHELL_CONFIG.SERVICE_DESK;
    return PORTAL_SHELL_CONFIG.CUSTOMER_ADMIN;
  });

  readonly visibleNavigationGroups = computed<PortalNavGroup[]>(() =>
    this.portal().navGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          const permissions = this.effectivePermissions();
          const capabilityOk =
            !item.capability || permissions.has(item.capability);
          const requiresOk =
            !item.requires?.length ||
            item.requires.every(code => permissions.has(code));
          const anyOfOk =
            !item.anyOf?.length ||
            item.anyOf.some(code => permissions.has(code));

          return capabilityOk && requiresOk && anyOfOk;
        })
      }))
      .filter(group => group.items.length > 0)
  );

  readonly flattenedNavigation = computed<PortalNavItem[]>(() =>
    this.visibleNavigationGroups().flatMap(group => group.items)
  );

  readonly searchResults = computed(() => {
    const value = this.searchQuery().trim().toLowerCase();
    const all = this.flattenedNavigation();
    return (value ? all.filter((item) => item.label.toLowerCase().includes(value)) : all).slice(0, 10);
  });

  readonly currentPageLabel = computed(() => {
    const url = this.currentUrl().split('?')[0];
    const exact = this.flattenedNavigation().find((item) => item.route === url);
    if (exact) return exact.label;

    const nested = [...this.flattenedNavigation()]
      .filter((item) => url.startsWith(item.route + '/'))
      .sort((a, b) => b.route.length - a.route.length)[0];

    return nested?.label ?? 'Inicio';
  });

  readonly portalOptions = computed(() => {
    const stored = this.authorizedDestinations();
    if (stored.length) return stored;

    return [{
      portalCode: this.portal().code,
      routePath: this.portal().route,
      displayName: this.portal().label,
      isPreferredPortal: true
    }];
  });

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeMenus();
        this.mobileSidebarOpen.set(false);
      });

    document.addEventListener(
      'fullscreenchange',
      () => this.fullscreen.set(!!document.fullscreenElement)
    );

    window.addEventListener(
      'deskplus:preferences-updated',
      this.experienceUpdateHandler
    );
  }

  ngOnInit(): void {
    this.loadAuthorizedDestinations();
    this.loadNavigationContext();
    this.loadExperiencePreferences();
    this.loadProfilePhoto();
    this.loadSlaNotifications();
  }

  ngOnDestroy(): void {
    this.idleLock.stop();
    window.removeEventListener(
      'deskplus:preferences-updated',
      this.experienceUpdateHandler
    );
  }

  toggleSidebar(): void {
    if (window.innerWidth <= 1024) {
      this.mobileSidebarOpen.update((value) => !value);
      return;
    }

    const next = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(next);
    localStorage.setItem(
      'deskplus.sidebar.collapsed',
      next ? '1' : '0'
    );
    this.persistExperiencePatch({ sidebarCollapsed: next });

    this.portalMenuOpen.set(false);
  }

  toggleGroup(index: number): void {
    if (this.portal().code !== 'SERVICE_DESK' && this.sidebarCollapsed()) {
      this.sidebarCollapsed.set(false);
      localStorage.setItem('deskplus.sidebar.collapsed', '0');
    }

    this.collapsedGroups.update((value) => ({ ...value, [index]: !value[index] }));
  }

  isGroupCollapsed(index: number): boolean {
    return !!this.collapsedGroups()[index];
  }

  toggleUserMenu(): void {
    const next = !this.userMenuOpen();
    this.closeMenus();
    this.userMenuOpen.set(next);
  }

  togglePortalMenu(): void {
    if (this.sidebarCollapsed()) {
      this.sidebarCollapsed.set(false);
      localStorage.setItem('deskplus.sidebar.collapsed', '0');
      setTimeout(() => this.portalMenuOpen.set(true), 120);
      return;
    }

    const next = !this.portalMenuOpen();
    this.closeMenus();
    this.portalMenuOpen.set(next);
  }

  togglePreferences(): void {
    const next = !this.preferencesOpen();
    this.closeMenus();
    this.preferencesOpen.set(next);
  }

  toggleLanguageMenu(): void {
    const next = !this.languageMenuOpen();
    this.closeMenus();
    this.languageMenuOpen.set(next);
  }

  toggleNotifications(): void {
    const next = !this.notificationMenuOpen();
    this.closeMenus();
    this.notificationMenuOpen.set(next);
    if (next) this.loadSlaNotifications();
  }

  markNotificationsRead(): void {
    this.http.post(`${environment.apiBaseUrl}/api/notifications/read-all`, {}).subscribe({
      next: () => this.notificationAlerts.update(items => items.map(item => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })))
    });
  }

  isNotificationUnread(id: string): boolean { return !this.notificationAlerts().find(item => item.id === id)?.readAt; }

  openNotification(alert: UserNotification): void {
    this.http.post(`${environment.apiBaseUrl}/api/notifications/${alert.id}/read`, {}).subscribe();
    this.notificationAlerts.update(items => items.map(item => item.id === alert.id ? { ...item, readAt: item.readAt ?? new Date().toISOString() } : item));
    this.notificationMenuOpen.set(false);
    void this.router.navigateByUrl(alert.routePath);
  }

  notificationDate(value: string): string {
    return new Intl.DateTimeFormat('es-PA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  openNotificationCenter(): void {
    this.notificationMenuOpen.set(false);
    if (this.portal().code === 'SERVICE_DESK') void this.router.navigateByUrl('/app/services/notifications');
    else if (this.portal().code === 'TECHNICIAN_PORTAL') void this.router.navigateByUrl('/app/technician/notifications');
    else void this.router.navigateByUrl('/app/admin/notifications');
  }

  switchPortal(destination: PortalDestination): void {
    if (!destination.routePath) return;
    this.portalMenuOpen.set(false);
    void this.router.navigateByUrl(destination.routePath);
  }

  openSearch(): void {
    this.searchOpen.set(true);
    this.searchQuery.set('');
    this.closeMenus();
    setTimeout(() => document.querySelector<HTMLInputElement>('#deskplus-global-search')?.focus());
  }

  closeSearch(): void {
    this.searchOpen.set(false);
    this.searchQuery.set('');
  }

  updateSearch(value: string): void {
    this.searchQuery.set(value);
  }

  navigateFromSearch(route: string): void {
    this.closeSearch();
    void this.router.navigateByUrl(route);
  }

  setLocale(locale: SupportedLocale): void {
    this.i18n.setLocale(locale);
    this.persistExperiencePatch({ languageCode: locale });
    this.languageMenuOpen.set(false);
  }

  setTheme(theme: DeskPlusTheme): void {
    this.preferences.setTheme(theme);
    this.persistExperiencePatch({
      theme: theme.toUpperCase() as UserExperiencePreferences['theme']
    });
  }

  setAccent(accent: DeskPlusAccent): void {
    this.preferences.setAccent(accent);
    this.persistExperiencePatch({
      accent: accent.toUpperCase() as UserExperiencePreferences['accent']
    });
  }

  async toggleFullscreen(): Promise<void> {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      this.fullscreen.set(false);
    }
  }

  openProfile(): void {
    this.closeMenus();
    void this.router.navigateByUrl(this.profileRoute());
  }

  openPersonalSettings(): void {
    this.closeMenus();
    void this.router.navigateByUrl(this.preferencesRoute());
  }

  openSecuritySessions(): void {
    this.closeMenus();
    void this.router.navigateByUrl(this.securityRoute());
  }

  lockSession(): void {
    if (this.lockingSession()) return;

    this.closeMenus();
    this.lockingSession.set(true);
    this.idleLock.stop();

    const returnUrl = this.currentUrl().startsWith('/app/')
      ? this.currentUrl()
      : this.portal().route;

    this.auth.lockScreen(returnUrl).subscribe({
      next: response => {
        if (response.locked) {
          void this.router.navigateByUrl('/lock');
          return;
        }

        this.lockingSession.set(false);
        this.startIdleLock();
      },
      error: () => {
        this.lockingSession.set(false);
        this.startIdleLock();
      }
    });
  }

  logout(): void {
    this.closeMenus();
    this.http.post(`${environment.apiBaseUrl}/api/auth/logout`, {}, { withCredentials: true }).subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout()
    });
  }

  closeMenus(): void {
    this.userMenuOpen.set(false);
    this.portalMenuOpen.set(false);
    this.preferencesOpen.set(false);
    this.languageMenuOpen.set(false);
    this.notificationMenuOpen.set(false);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyboard(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openSearch();
      return;
    }

    if (event.key === 'Escape') {
      this.closeMenus();
      this.mobileSidebarOpen.set(false);
      this.closeSearch();
    }
  }

  private loadAuthorizedDestinations(): void {
    try {
      const raw = sessionStorage.getItem('deskplus.authorized_destinations');
      if (!raw) return;

      const destinations = JSON.parse(raw) as PortalDestination[];
      if (Array.isArray(destinations)) {
        this.authorizedDestinations.set(destinations.filter((item) => !!item.routePath));
      }
    } catch {
      this.authorizedDestinations.set([]);
    }
  }

  private loadNavigationContext(): void {
    this.userSelfService.getNavigationContext().subscribe({
      next: context => {
        this.effectiveRoles.set(new Set(context.roleCodes));
        this.effectivePermissions.set(new Set(context.permissionCodes));
      },
      error: () => {
        this.effectiveRoles.set(new Set());
        this.effectivePermissions.set(new Set());
      }
    });
  }

  private loadExperiencePreferences(): void {
    this.userSelfService.getPreferences().subscribe({
      next: preferences => {
        this.applyExperiencePreferences(preferences);
      },
      error: () => {
        this.screenLockMinutes.set(15);
        this.startIdleLock();
      }
    });
  }

  private applyExperiencePreferences(
    preferences: UserExperiencePreferences
  ): void {
    this.experiencePreferences.set(preferences);
    this.screenLockMinutes.set(preferences.screenLockMinutes);
    this.sidebarCollapsed.set(preferences.sidebarCollapsed);

    localStorage.setItem(
      'deskplus.sidebar.collapsed',
      preferences.sidebarCollapsed ? '1' : '0'
    );

    this.preferences.applyRemote(
      preferences.theme,
      preferences.accent,
      preferences.density,
      preferences.reduceMotion
    );

    const language = preferences.languageCode?.toLowerCase();
    if (language === 'es' || language?.startsWith('es-')) {
      this.i18n.setLocale('es');
    } else if (language === 'en' || language?.startsWith('en-')) {
      this.i18n.setLocale('en');
    }

    this.startIdleLock();
  }

  private persistExperiencePatch(
    patch: Partial<UserExperiencePreferences>
  ): void {
    const current = this.experiencePreferences();
    if (!current) return;

    const next: UserExperiencePreferences = {
      ...current,
      ...patch
    };

    this.experiencePreferences.set(next);

    this.userSelfService.updatePreferences(next).subscribe({
      next: saved => this.applyExperiencePreferences(saved),
      error: () => {
        // Keep the current local experience responsive. The dedicated
        // preferences page surfaces persistence errors to the user.
      }
    });
  }

  private startIdleLock(): void {
    this.idleLock.start(
      this.screenLockMinutes(),
      () => this.lockSession()
    );
  }

  private loadProfilePhoto(): void {
    this.userSelfService.getProfile().subscribe({
      next: profile => this.profilePhotoUrl.set(profile.profilePhotoUrl ?? null),
      error: () => this.profilePhotoUrl.set(null)
    });
  }

  private loadSlaNotifications(): void {
    if (this.notificationsLoading()) return;
    this.notificationsLoading.set(true);
    this.http.get<UserNotification[]>(`${environment.apiBaseUrl}/api/notifications/mine`).subscribe({
      next: alerts => { this.notificationAlerts.set(alerts); this.updateUnreadCount(); this.notificationsLoading.set(false); },
      error: () => { this.notificationAlerts.set([]); this.unreadNotifications.set(0); this.notificationsLoading.set(false); }
    });
  }

  private updateUnreadCount(): void {
    this.unreadNotifications.set(this.notificationAlerts().filter(x => !x.readAt).length);
  }

  private profileRoute(): string {
    if (this.portal().code === 'CUSTOMER_ADMIN') return '/app/admin/profile';
    if (this.portal().code === 'TECHNICIAN_PORTAL') return '/app/technician/profile';
    if (this.portal().code === 'SERVICE_DESK') return '/app/services/profile';
    return '/app/niotech/profile';
  }

  private securityRoute(): string {
    if (this.portal().code === 'CUSTOMER_ADMIN') return '/app/admin/security';
    if (this.portal().code === 'TECHNICIAN_PORTAL') return '/app/technician/account-security';
    if (this.portal().code === 'SERVICE_DESK') return '/app/services/account-security';
    return '/app/niotech/security';
  }

  private preferencesRoute(): string {
    if (this.portal().code === 'CUSTOMER_ADMIN') return '/app/admin/preferences';
    if (this.portal().code === 'TECHNICIAN_PORTAL') return '/app/technician/preferences';
    if (this.portal().code === 'SERVICE_DESK') return '/app/services/preferences';
    return '/app/niotech/preferences';
  }

  private finishLogout(): void {
    sessionStorage.removeItem('deskplus.access_token');
    sessionStorage.removeItem('deskplus.authorized_destinations');
    void this.router.navigateByUrl('/login');
  }
}
